import { useState, useRef, useCallback } from "react";

interface ProductImageZoomProps {
  src: string;
  alt: string;
  images?: string[];
}

export default function ProductImageZoom({ src, alt, images }: ProductImageZoomProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [isZooming, setIsZooming] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const allImages = images && images.length > 0 ? images : [src];
  const currentImage = allImages[activeIndex];

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setZoomPosition({ x, y });
    },
    []
  );

  const handleMouseEnter = () => setIsZooming(true);
  const handleMouseLeave = () => setIsZooming(false);

  const hasMultiple = allImages.length > 1;

  return (
    <div className="flex flex-col gap-4">
      {/* Desktop: Inner Zoom */}
      <div
        ref={containerRef}
        className="hidden md:block relative w-full overflow-hidden rounded-lg cursor-zoom-in"
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => setShowLightbox(true)}
      >
        <div className="w-full h-[520px]">
          <img
            src={currentImage}
            alt={alt}
            className="w-full h-full object-cover object-top transition-transform duration-200"
            style={
              isZooming
                ? {
                    transform: "scale(2)",
                    transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                  }
                : {}
            }
          />
        </div>
      </div>

      {/* Mobile: Tap to Lightbox */}
      <div
        className="md:hidden relative w-full overflow-hidden rounded-lg"
        onClick={() => setShowLightbox(true)}
      >
        <div className="w-full h-72">
          <img src={currentImage} alt={alt} className="w-full h-full object-cover object-top" />
        </div>
        <div className="absolute bottom-3 right-3 bg-brand-darkbrown/60 backdrop-blur-sm text-brand-cream font-display text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full border border-brand-gold/30">
          Tap to Zoom
        </div>
        {hasMultiple && (
          <div className="absolute bottom-3 left-3 bg-brand-darkbrown/60 backdrop-blur-sm text-brand-cream font-display text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full border border-brand-gold/30">
            {activeIndex + 1} / {allImages.length}
          </div>
        )}
      </div>

      {/* Thumbnail Strip */}
      {hasMultiple && (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {allImages.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setActiveIndex(idx)}
              className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer ${
                activeIndex === idx
                  ? "border-brand-gold opacity-100"
                  : "border-transparent opacity-60 hover:opacity-90"
              }`}
            >
              <img
                src={img}
                alt={`${alt} view ${idx + 1}`}
                className="w-full h-full object-cover object-top"
              />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {showLightbox && (
        <div
          className="fixed inset-0 z-[100] bg-brand-darkbrown/95 flex flex-col items-center justify-center p-4 md:p-8"
          onClick={() => setShowLightbox(false)}
        >
          <button
            className="absolute top-6 right-6 text-brand-cream/80 hover:text-brand-cream transition-colors duration-200 cursor-pointer w-10 h-10 flex items-center justify-center"
            onClick={() => setShowLightbox(false)}
            aria-label="Close lightbox"
          >
            <i className="ri-close-line text-2xl" />
          </button>

          {hasMultiple && (
            <button
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-brand-cream/70 hover:text-brand-cream transition-colors duration-200 cursor-pointer w-10 h-10 flex items-center justify-center bg-brand-darkbrown/50 rounded-full border border-brand-gold/20"
              onClick={(e) => {
                e.stopPropagation();
                setActiveIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
              }}
              aria-label="Previous image"
            >
              <i className="ri-arrow-left-s-line text-xl" />
            </button>
          )}

          {hasMultiple && (
            <button
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-brand-cream/70 hover:text-brand-cream transition-colors duration-200 cursor-pointer w-10 h-10 flex items-center justify-center bg-brand-darkbrown/50 rounded-full border border-brand-gold/20"
              onClick={(e) => {
                e.stopPropagation();
                setActiveIndex((prev) => (prev + 1) % allImages.length);
              }}
              aria-label="Next image"
            >
              <i className="ri-arrow-right-s-line text-xl" />
            </button>
          )}

          <div
            className="relative w-full max-w-5xl h-full max-h-[80vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentImage}
              alt={alt}
              className="w-full h-full object-contain"
            />
            {hasMultiple && (
              <div className="mt-4 flex items-center gap-4">
                <p className="font-display text-brand-cream/70 text-xs tracking-widest uppercase">
                  {activeIndex + 1} / {allImages.length}
                </p>
                <div className="flex gap-2">
                  {allImages.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveIndex(idx);
                      }}
                      className={`w-2 h-2 rounded-full transition-all duration-200 cursor-pointer ${
                        activeIndex === idx ? "bg-brand-gold w-4" : "bg-brand-cream/30"
                      }`}
                      aria-label={`Go to image ${idx + 1}`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}